import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { PublicPageShell } from "../components/PublicPageShell";
import { StoreBuyButton } from "../components/store/StoreBuyButton";
import { MobileStickyCta } from "../components/MobileStickyCta";
import { useProductVaultCheckout } from "../hooks/useProductVaultCheckout";
import { StoreFeatureTiles, StoreWhatsInside, StoreHonestProof, StoreFaq } from "../components/store/StoreSections";
import { StoreDemoPlayer } from "../components/store/StoreDemoPlayer";
import { StoreTrustStrip, StoreDeliveryFacts, productCopy } from "../components/store/StoreProductFacts";
import { useStoreProduct, gbp } from "../lib/store";
import { trackEvent } from "../lib/analytics";
import { VAULT } from "../data/vaultStats";

const BULLETS = ["Instant digital download + unlocked on your Stag Vault account", "Copy-ready · Run with AI inside the vault", "One-time payment — yours to keep"];

function StoreStickyBuy({ product }) {
  const { buying, buyTrack } = useProductVaultCheckout(product.slug);
  if (product.owned || !product.purchasable) return null;
  return <MobileStickyCta label={product.name.replace(" (Bonus)", "")} note={`${product.prompt_count} prompts · one-time`} price={`Buy ${gbp(product.price)}`} loading={buying} onClick={() => buyTrack("store_sticky")} testId="store-sticky-cta" />;
}

function ProductHero({ product }) {
  const copy = productCopy(product);
  return (
    <section className="relative overflow-hidden border border-zinc-900 bg-black max-w-full" data-testid="store-product-hero">
      <img src="/store/vault-hero.webp" alt="" aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-auto max-w-none opacity-40 md:opacity-60 pointer-events-none select-none" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,#000_35%,rgba(0,0,0,0.75)_60%,rgba(0,0,0,0.2)_100%)]" />
      <div className="relative grid md:grid-cols-[1.1fr_0.9fr] gap-10 p-6 sm:p-8 md:p-14 items-center min-w-0">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-purple-300">Track {product.order} · Single-track purchase</div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.98] tracking-tight" data-testid="store-product-title">
            <span className="text-white">{copy.h1.replace(/ – Single Track$/, "")}</span><br /><span className="text-purple-300">– Single Track</span>
          </h1>
          <p className="mt-4 text-lg md:text-xl font-semibold text-white" data-testid="store-product-benefit">{copy.benefit}</p>
          {product.tagline && product.tagline !== copy.benefit && <p className="mt-3 text-base text-zinc-400 max-w-lg">{product.tagline}</p>}
          <ul className="mt-6 space-y-2">
            {BULLETS.map((b) => <li key={b} className="flex items-center gap-3 text-sm text-zinc-400"><Check size={14} className="text-purple-400" />{b}</li>)}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-5 min-w-0">
            <StoreBuyButton product={product} source="store_product" />
            <div className="text-zinc-500 text-xs font-mono" data-testid="store-product-price">{gbp(product.price)} one-time · {product.prompt_count} prompts · GBP</div>
          </div>
          <StoreTrustStrip product={product} />
        </div>
        <div className="relative flex justify-center md:justify-end">
          <img src={product.image || "/store/operator-power-prompts-box.webp"} alt={`${product.name} box art`} className="w-52 sm:w-64 md:w-72 drop-shadow-[0_30px_60px_rgba(168,85,247,0.35)] store-float" data-testid="store-product-image" />
        </div>
      </div>
    </section>
  );
}

export default function StoreProductPage() {
  const { slug } = useParams();
  const { product, loading, missing } = useStoreProduct(slug);
  useEffect(() => { if (product) trackEvent("StoreProductView", { slug }); }, [product, slug]);

  return (
    <PublicPageShell crumbs={[{ label: "Store", href: "/store" }, { label: (product?.name || "Product").replace(/\s*\(.*?\)\s*$/, "") }]} testId="store-product-page">
      <Link to="/store" className="inline-flex items-center gap-2 text-zinc-500 hover:text-purple-300 text-sm mb-8 transition-colors" data-testid="store-back-link"><ArrowLeft size={14} /> All products</Link>
      {loading && <div className="text-zinc-500 py-20" data-testid="store-loading">Loading…</div>}
      {missing && <div className="py-20" data-testid="store-missing"><h1 className="font-display text-3xl font-black">Product not found</h1><Link to="/store" className="text-purple-300 underline mt-4 inline-block">Back to the store</Link></div>}
      {product && (
        <>
          <ProductHero product={product} />
          <StoreDeliveryFacts product={product} />
          <div className="mt-12"><StoreFeatureTiles product={product} /></div>
          <StoreDemoPlayer />
          <StoreWhatsInside product={product} />
          <StoreHonestProof product={product} />
          <StoreFaq product={product} />
          <section className="mt-20 border border-purple-500/40 bg-purple-500/[0.05] p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="store-final-cta">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-300">Ready?</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-black tracking-tight">Unlock {product.name.replace(" (Bonus)", "")} for {gbp(product.price)}.</h2>
              <p className="mt-2 text-sm text-zinc-400">Or get every track — all {VAULT.tracks} — with Stag Vault Complete (Lifetime Access) for £{VAULT.price.lifetime}.</p>
            </div>
            <StoreBuyButton product={product} source="store_product_footer" />
          </section>
          <StoreStickyBuy product={product} />
        </>
      )}
    </PublicPageShell>
  );
}
